"use client";

import { useEffect, useState } from "react";
import ExperimentSection from "./ExperimentSection";

interface Subsection {
  key: string;
  title: string;
  description: string;
}

interface Section {
  section: string;
  title: string;
  icon: string;
  content?: string;
  subsections: Subsection[];
  version?: number;
}

interface PortalSectionsProps {
  experimentTrigger?: number;
  onNavigateToPdf?: (id: string, title: string) => void;
}

export default function PortalSections({ experimentTrigger, onNavigateToPdf }: PortalSectionsProps = {}) {
  const [sections, setSections] = useState<Section[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [experimentOpen, setExperimentOpen] = useState(false);

  useEffect(() => {
    if (!experimentTrigger) return;
    setExperimentOpen(true);
    setTimeout(() => {
      document.getElementById("eksperiment-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }, [experimentTrigger]);

  useEffect(() => {
    fetch(`/api/portal/sections`)
      .then((r) => r.json())
      .then((data) => {
        setSections(data.sections || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Fallback sections while API starts
  const displaySections = sections.length > 0 ? sections : [
    {
      section: "facilitering", title: "Facilitering af Partnerskaber og Dialog", icon: "🤝",
      subsections: [
        { key: "klimaforum", title: "Faxe Klimaforum", description: "Digitalt forum for alle interesserede, som kan deltage i debatten og dele idéer om klimatiltag." },
        { key: "netvaerk", title: "Netværk for Interessenter", description: "Rum for dialog mellem borgere, virksomheder, foreninger, investorer og myndigheder." },
        { key: "klimatilpasning", title: "Inddragelse i Klimatilpasning", description: "Samarbejde om kystbeskyttelse og håndtering af oversvømmelser." },
      ],
    },
    {
      section: "information", title: "Målrettet Information", icon: "📢",
      subsections: [
        { key: "borgere", title: "Borgere og Boligejere", description: "Vejledning om varmepumper, energibesparelser og tilskudsmuligheder." },
        { key: "erhvervsliv", title: "Erhvervsliv og Landbrug", description: "Erhvervsstrategier, ledige erhvervsgrunde og dialogfora." },
        { key: "udsatte", title: "Særligt Udsatte Grupper", description: "Forenklet hjælp til ressourcesvage eller ældre medborgere." },
      ],
    },
    {
      section: "groenne_vaerktoejer", title: "Grøn Livsstil og Transport", icon: "🌱",
      subsections: [
        { key: "mobilitet", title: "Mobilitetshub", description: "Elbil-ladepunkter, samkørsel og delebiler." },
        { key: "affald", title: "Affald og Genbrug", description: "Sortering og AffaldPlus samt Plus-Butikker." },
        { key: "natur", title: "Natur og Sundhed", description: "Kort over stier, ruter og grønne områder." },
      ],
    },
    {
      section: "monitorering", title: "Monitorering og Gennemsigtighed", icon: "📊",
      subsections: [
        { key: "klimaregnskab", title: "Klimaregnskab", description: "Årlige klimaregnskaber og status på 70%-reduktionsmål i 2030." },
        { key: "kommune", title: "Kommunen som Virksomhed", description: "Data om energineutral bygningsdrift og elektrificering af bilparken." },
        { key: "byudvikling", title: "Byudvikling og Planlægning", description: "Kommuneplanens retningslinjer for vedvarende energianlæg." },
      ],
    },
  ];

  const sectionColors: Record<string, string> = {
    facilitering: "#2d6a4f",
    information: "#1e3a5f",
    groenne_vaerktoejer: "#52b788",
    monitorering: "#d4a017",
  };

  return (
    <section className="py-16 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Community Portal
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Fire søjler for en bæredygtig fremtid i Faxe Kommune.
            Vælg en sektion for at lære mere.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {displaySections.map((section) => {
            const color = sectionColors[section.section] || "#2d6a4f";
            const isSelected = selected === section.section;

            return (
              <div
                key={section.section}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all duration-200"
              >
                {/* Section header */}
                <button
                  className="w-full text-left p-6 flex items-start gap-4"
                  onClick={() => setSelected(isSelected ? null : section.section)}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    style={{ backgroundColor: color + "20" }}
                  >
                    {section.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 text-lg mb-1">
                      {section.title}
                    </h3>
                    {section.content && (
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {section.content.slice(0, 120)}...
                      </p>
                    )}
                    {section.version && (
                      <span className="text-xs text-gray-400 mt-1 block">
                        Version {section.version}
                      </span>
                    )}
                  </div>
                  <div
                    className="text-gray-400 transition-transform duration-200"
                    style={{ transform: isSelected ? "rotate(180deg)" : "rotate(0deg)" }}
                  >
                    ▼
                  </div>
                </button>

                {/* Subsections */}
                {isSelected && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <div className="pt-4 grid gap-3">
                      {section.subsections.map((sub) => (
                        <div
                          key={sub.key}
                          className="p-4 rounded-xl border border-gray-100 hover:border-opacity-50 transition-colors cursor-pointer"
                          style={{ borderLeftWidth: 3, borderLeftColor: color }}
                        >
                          <div className="font-semibold text-gray-800 text-sm mb-1">
                            {sub.title}
                          </div>
                          <div className="text-gray-600 text-sm leading-relaxed">
                            {sub.description}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Color accent bar at bottom */}
                <div className="h-1" style={{ backgroundColor: color }} />
              </div>
            );
          })}
        </div>

        {/* ── Eksperiment – full-width AI section ── */}
        <div id="eksperiment-section" className="mt-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md">
            {/* Header */}
            <button
              className="w-full text-left p-6 flex items-start gap-4"
              onClick={() => setExperimentOpen((v) => !v)}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 bg-purple-100">
                🧪
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg mb-1">Eksperiment</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  AI-assisteret forretningsmodel canvas og projektøkonomi — byg, analyser og gem dine idéer.
                </p>
              </div>
              <div
                className="text-gray-400 transition-transform duration-200 mt-1"
                style={{ transform: experimentOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              >
                ▼
              </div>
            </button>

            {/* Expanded content */}
            {experimentOpen && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-5">
                <ExperimentSection onNavigateToPdf={onNavigateToPdf} />
              </div>
            )}

            {/* Purple accent bar */}
            <div className="h-1 bg-purple-600" />
          </div>
        </div>

      </div>
    </section>
  );
}
